import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/auth/AuthShell";

const Status = ({ ok, text, testId }) => (
  <div className={`flex items-start gap-3 rounded-xl p-4 ${ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid={testId}>
    {ok ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}<p className="text-sm">{text}</p>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); } finally { setBusy(false); setSent(true); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a link to choose a new password." testId="forgot-password-page">
      {sent ? <Status ok text="If an account exists for that email, a reset link is on its way. It expires in 1 hour." testId="forgot-password-sent" /> : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" data-testid="forgot-email-input" /></div>
          <Button disabled={busy} className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="forgot-submit-btn">Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-semibold text-[#00A85C] hover:underline" data-testid="back-to-login-link">Back to log in</Link></p>
    </AuthShell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ done: false, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      setState({ done: true, error: "" });
    } catch (err) {
      setState({ done: false, error: formatError(err) });
    }
  };
  return (
    <AuthShell title="Choose a new password" testId="reset-password-page">
      {state.done ? <Status ok text="Your password has been updated. You can now log in." testId="reset-success" /> : (
        <form onSubmit={submit} className="space-y-4">
          {state.error && <Alert variant="destructive" data-testid="reset-error"><AlertDescription>{state.error}</AlertDescription></Alert>}
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl" data-testid="reset-password-input" /></div>
          <Button className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="reset-submit-btn">Update password</Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-semibold text-[#00A85C] hover:underline">Go to log in</Link></p>
    </AuthShell>
  );
};

export const VerifyEmail = () => {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) { return; }
    ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState({ ok: true }); refresh(); })
      .catch((err) => setState({ ok: false, text: formatError(err) }));
  }, [params, refresh]);
  return (
    <AuthShell title="Email verification" testId="verify-email-page">
      {!state ? <p className="text-slate-500">Verifying…</p> : <Status ok={state.ok} text={state.ok ? "Your email is verified. Let's set up your business." : state.text} testId="verify-email-status" />}
      <Link to={user ? "/app/onboarding" : "/login?next=/app/onboarding"}><Button className="mt-6 rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="verify-continue-btn">Continue</Button></Link>
    </AuthShell>
  );
};

export const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh, switchBusiness } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/invitations/lookup/${token}`).then((r) => setInvite(r.data)).catch((err) => setError(formatError(err)));
  }, [token]);
  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/invitations/accept", { token });
      switchBusiness(data.business_id);
      await refresh();
      navigate("/app", { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  const next = encodeURIComponent(`/invite/${token}`);
  return (
    <AuthShell title={invite ? `Join ${invite.business_name}` : "Team invitation"} subtitle={invite ? `You've been invited as ${invite.role === "manager" ? "a manager" : "an employee"}.` : null} testId="accept-invite-page">
      {error && <Alert variant="destructive" className="mb-4" data-testid="invite-error"><AlertDescription>{error}</AlertDescription></Alert>}
      {invite && (user ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Signed in as <strong>{user.email}</strong>.</p>
          <Button onClick={accept} disabled={busy} className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="accept-invite-btn">{busy ? "Joining…" : "Accept invitation"}</Button>
        </div>
      ) : user === false ? (
        <div className="space-y-3">
          <Link to={`/register?next=${next}${invite.email ? `&email=${encodeURIComponent(invite.email)}` : ""}`}><Button className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="invite-register-btn">Create my account</Button></Link>
          <Link to={`/login?next=${next}`}><Button variant="outline" className="mt-3 h-11 w-full rounded-xl" data-testid="invite-login-btn">I already have an account</Button></Link>
        </div>
      ) : null)}
    </AuthShell>
  );
};

export const GoogleCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applyMe } = useAuth();
  const [error, setError] = useState("");
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) { return; }
    processed.current = true;
    const next = sessionStorage.getItem("clockaroo.next") || "/app";
    sessionStorage.removeItem("clockaroo.next");
    if (params.get("error") || !params.get("code")) { setError("Google sign-in was cancelled."); return; }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    api.post("/auth/google/callback", { code: params.get("code"), state: params.get("state") || "", redirect_uri: redirectUri })
      .then(({ data }) => {
        applyMe(data);
        navigate(next, { replace: true });
      })
      .catch((err) => setError(formatError(err)));
  }, [params, navigate, applyMe]);
  return (
    <AuthShell title={error ? "Sign-in failed" : "Signing you in…"} testId="google-callback-page">
      {error ? <><Status ok={false} text={error} testId="google-callback-error" /><Link to="/login"><Button className="mt-6 rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="google-callback-back-btn">Back to log in</Button></Link></> : <p className="text-slate-500">Just a moment.</p>}
    </AuthShell>
  );
};
